import { asInstanceOf, Option, Terminable, Terminator, tryCatch, UUID } from "@opendaw/lib-std";
import { PPQN, RenderQuantum, TimeBase } from "@opendaw/lib-dsp";
import { AudioFileBox, AudioRegionBox, TrackBox, ValueEventCollectionBox } from "@opendaw/studio-boxes";
import { ColorCodes, TrackType, UnionBoxTypes } from "@opendaw/studio-adapters";
import { Recording } from "./Recording";
import { RecordTrack } from "./RecordTrack";
export var RecordAudio;
(function (RecordAudio) {
    const ANCHOR_WAIT_SECONDS = 0.25;
    RecordAudio.start = ({ recordingWorklet, sourceNode, sampleManager, project, capture, readLatency }) => {
        const startLatency = readLatency();
        console.debug("[RecordAudio] start", startLatency);
        const terminator = new Terminator();
        const { editing, engine, boxGraph, timelineBox, tempoMap } = project;
        const fileUuid = recordingWorklet.uuid;
        // Note: sampleManager.record() and sourceNode.connect() are called in prepareRecording
        let fileBox = Option.None;
        let currentTake = Option.None;
        let lastPosition = 0;
        let currentWaveformOffset = 0;
        let takeNumber = 0;
        let firstRecordingTick = Option.None;
        const { env: { audioContext }, engine: { preferences: { settings: { recording } } } } = project;
        const { sampleRate } = audioContext;
        const { loopArea } = timelineBox;
        const minTakeSeconds = RenderQuantum / sampleRate;
        const createFileBox = () => {
            const fileDateString = new Date()
                .toISOString()
                .replaceAll("T", "-")
                .replaceAll(".", "-")
                .replaceAll(":", "-")
                .replaceAll("Z", "");
            const fileName = `Recording-${fileDateString}`;
            return AudioFileBox.create(boxGraph, fileUuid, box => box.fileName.setValue(fileName));
        };
        const createTakeRegion = (position, waveformOffset, excludeTrack) => {
            takeNumber++;
            console.debug("[RecordAudio] createTakeRegion", { takeNumber, position, waveformOffset });
            const trackBox = RecordTrack.findOrCreate(editing, capture.audioUnitBox, TrackType.Audio, excludeTrack);
            const collectionBox = ValueEventCollectionBox.create(boxGraph, UUID.generate());
            const regionBox = AudioRegionBox.create(boxGraph, UUID.generate(), box => {
                box.file.refer(fileBox.unwrap("fileBox"));
                box.events.refer(collectionBox.owners);
                box.regions.refer(trackBox.regions);
                box.position.setValue(position);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Audio));
                box.timeBase.setValue(TimeBase.Seconds);
                box.label.setValue(`Take ${takeNumber}`);
                box.waveformOffset.setValue(waveformOffset);
                box.duration.setValue(minTakeSeconds);
                box.loopDuration.setValue(minTakeSeconds);
            });
            capture.addRecordedRegion(regionBox);
            project.selection.select(regionBox);
            return { trackBox, regionBox };
        };
        const finalizeTake = (take, durationInSeconds) => {
            console.debug("[RecordAudio] finalizeTake", { durationInSeconds });
            const { trackBox, regionBox } = take;
            if (regionBox.isAttached()) {
                // The finalized length is recomputed (loop boundary), so unlike the live update it can be
                // non-positive for a take that started at/after loopTo. Never persist a zero/negative
                // duration region (it later trips validateTrack "duration must be positive") — drop it.
                if (durationInSeconds <= 0) {
                    console.debug("[RecordAudio] finalizeTake: dropping non-positive take", { durationInSeconds });
                    regionBox.delete();
                    return;
                }
                regionBox.duration.setValue(durationInSeconds);
                regionBox.loopDuration.setValue(durationInSeconds);
            }
            const { olderTakeAction, olderTakeScope } = recording;
            if (olderTakeScope === "none") {
                return;
            }
            if (olderTakeScope === "all") {
                for (const track of capture.audioUnitBox.tracks.pointerHub.incoming()
                    .map(({ box }) => asInstanceOf(box, TrackBox))) {
                    const trackType = track.type.getValue();
                    if (trackType === TrackType.Value) {
                        continue;
                    }
                    if (track === trackBox) {
                        continue;
                    }
                    if (olderTakeAction === "disable-track") {
                        if (track.isAttached()) {
                            track.enabled.setValue(false);
                        }
                    }
                    else {
                        for (const region of track.regions.pointerHub.incoming()
                            .map(({ box }) => UnionBoxTypes.asRegionBox(box))) {
                            if (region.isAttached()) {
                                region.mute.setValue(true);
                            }
                        }
                    }
                }
            }
            else {
                if (olderTakeAction === "disable-track") {
                    if (trackBox.isAttached()) {
                        trackBox.enabled.setValue(false);
                    }
                }
                else {
                    if (regionBox.isAttached()) {
                        regionBox.mute.setValue(true);
                    }
                }
            }
        };
        const startNewTake = (position) => {
            const previousTrack = currentTake.mapOr(take => take.trackBox, null);
            currentTake = Option.wrap(createTakeRegion(position, currentWaveformOffset, previousTrack));
        };
        recordingWorklet.onSaved = () => {
            project.trackUserCreatedSample(fileUuid);
            editing.mark();
        };
        terminator.ownAll(Terminable.create(() => {
            tryCatch(() => sourceNode.disconnect(recordingWorklet));
            const numberOfFrames = recordingWorklet.numberOfFrames;
            const totalSeconds = numberOfFrames / sampleRate;
            // fixes #840: short recordings (e.g. count-in) can leave zero-duration regions
            currentTake.ifSome(({ regionBox }) => {
                if (!regionBox.isAttached()) {
                    return;
                }
                const takeSeconds = totalSeconds - currentWaveformOffset;
                if (takeSeconds <= 0) {
                    console.debug("[RecordAudio] stop: deleting zero-duration region", { takeNumber });
                    editing.modify(() => regionBox.delete(), false);
                    currentTake = Option.None;
                }
                else {
                    editing.modify(() => {
                        regionBox.duration.setValue(takeSeconds);
                        regionBox.loopDuration.setValue(takeSeconds);
                    }, false);
                }
            });
            const hasTakes = fileBox.mapOr(box => box.isAttached() && !box.pointerHub.isEmpty(), false);
            if (numberOfFrames === 0 || !hasTakes) {
                console.debug("[RecordAudio] abort", { numberOfFrames, hasTakes });
                sampleManager.remove(fileUuid);
                recordingWorklet.terminate();
                fileBox.ifSome(box => {
                    if (box.isAttached()) {
                        editing.modify(() => box.delete(), false);
                    }
                });
            }
            else {
                console.debug("[RecordAudio] stop", { takeNumber, totalSeconds, numberOfFrames });
                // keep every delivered frame: a limit above them would never be reached and the recording never finalize
                recordingWorklet.limit(numberOfFrames);
                fileBox.ifSome(box => {
                    if (box.isAttached()) {
                        box.endInSeconds.setValue(totalSeconds);
                    }
                });
            }
        }), engine.position.catchupAndSubscribe(owner => {
            const isCountingIn = engine.isCountingIn.getValue();
            const isRecording = engine.isRecording.getValue();
            if (!isCountingIn && !isRecording) {
                return;
            }
            const currentPosition = owner.getValue();
            if (isCountingIn) {
                return;
            }
            // From here on, isRecording is true
            const loopEnabled = loopArea.enabled.getValue();
            const loopFrom = loopArea.from.getValue();
            const allowTakes = project.engine.preferences.settings.recording.allowTakes;
            if (loopEnabled && allowTakes && currentTake.nonEmpty() && currentPosition < lastPosition) {
                // Compute the take's length from its own start position to
                // loopTo, not from loopFrom. When recording begins mid-loop,
                // the first take only spans [takePosition, loopTo]; using
                // the full loop length would overshoot by (takePosition - loopFrom).
                // Stays deterministic (avoids the latency-lagged live
                // regionBox.duration that previously caused peak drift):
                // subsequent takes start at loopFrom, so this evaluates to
                // the full loop length exactly as before.
                const loopTo = loopArea.to.getValue();
                editing.modify(() => {
                    currentTake.ifSome(take => {
                        if (recordingWorklet.numberOfFrames / sampleRate - currentWaveformOffset <= 0) {
                            take.regionBox.delete();
                            currentTake = Option.None;
                            return;
                        }
                        const takeDurationSeconds = tempoMap.intervalToSeconds(take.regionBox.position.getValue(), loopTo);
                        finalizeTake(take, takeDurationSeconds);
                        currentWaveformOffset += takeDurationSeconds;
                    });
                    if (currentTake.nonEmpty()) {
                        startNewTake(loopFrom);
                    }
                }, false);
            }
            lastPosition = currentPosition;
            // Create fileBox and region together when recording starts.
            if (fileBox.isEmpty()) {
                if (firstRecordingTick.isEmpty()) {
                    firstRecordingTick = Option.wrap(audioContext.currentTime);
                }
                const recordingStart = engine.recordingStart;
                const firstQuantumTime = recordingWorklet.firstQuantumTime;
                let takePosition;
                let waveformOffset;
                if (recordingStart.nonEmpty() && firstQuantumTime.nonEmpty()) {
                    const { contextTime, position } = recordingStart.unwrap();
                    const placedLatency = readLatency();
                    const placed = Number.isFinite(placedLatency.outputLatency)
                        && placedLatency.outputLatency > 0;
                    const { outputLatency, inputLatency } = placed ? placedLatency : startLatency;
                    console.debug(`[RecordAudio] outputLatency=${outputLatency} inputLatency=${inputLatency} `
                        + `source=${placed ? "placement" : "start"}`);
                    const startOffset = contextTime - firstQuantumTime.unwrap() + outputLatency + inputLatency;
                    // region position is an integer field: the fraction moves into the offset
                    takePosition = Math.floor(position);
                    waveformOffset = startOffset - tempoMap.intervalToSeconds(takePosition, position);
                    // the first captured frame postdates the start: begin at the first position the audio covers
                    if (waveformOffset < 0) {
                        const startSeconds = tempoMap.ppqnToSeconds(takePosition);
                        const coveredFrom = takePosition
                            + tempoMap.intervalToPPQN(startSeconds, startSeconds - waveformOffset);
                        takePosition = Math.ceil(coveredFrom);
                        waveformOffset = tempoMap.intervalToSeconds(coveredFrom, takePosition);
                    }
                    console.debug(`[RecordAudio] anchored: contextTime=${contextTime} position=${position} `
                        + `firstQuantumTime=${firstQuantumTime.unwrap()} takePosition=${takePosition} `
                        + `waveformOffset=${waveformOffset}`);
                }
                else if (audioContext.currentTime - firstRecordingTick.unwrap() < ANCHOR_WAIT_SECONDS) {
                    // both reports ride their own channel and may trail this callback by a tick: wait, then fall back
                    return;
                }
                else {
                    const countedIn = Recording.wasCountingIn();
                    const barPPQN = PPQN.fromSignature(timelineBox.signature.nominator.getValue(), timelineBox.signature.denominator.getValue());
                    const countInSeconds = countedIn
                        ? PPQN.pulsesToSeconds(recording.countInBars * barPPQN, timelineBox.bpm.getValue())
                        : 0;
                    const wallclockSinceWorklet = recordingWorklet.numberOfFrames / sampleRate;
                    const headStartSeconds = countedIn
                        ? Math.max(0, wallclockSinceWorklet - countInSeconds)
                        : wallclockSinceWorklet;
                    takePosition = currentPosition;
                    waveformOffset = headStartSeconds + countInSeconds
                        + startLatency.outputLatency + startLatency.inputLatency;
                    console.debug(`[RecordAudio] anchor fallback: recordingStart=${recordingStart.nonEmpty()} `
                        + `firstQuantumTime=${firstQuantumTime.nonEmpty()} takePosition=${takePosition} `
                        + `waveformOffset=${waveformOffset}`);
                }
                editing.modify(() => {
                    fileBox = Option.wrap(createFileBox());
                    currentTake = Option.wrap(createTakeRegion(takePosition, waveformOffset, null));
                }, false);
                currentWaveformOffset = waveformOffset;
            }
            currentTake.ifSome(({ regionBox }) => editing.modify(() => {
                if (regionBox.isAttached()) {
                    const { duration, loopDuration } = regionBox;
                    const totalSeconds = recordingWorklet.numberOfFrames / sampleRate;
                    const takeSeconds = Math.max(minTakeSeconds, totalSeconds - currentWaveformOffset);
                    duration.setValue(takeSeconds);
                    loopDuration.setValue(takeSeconds);
                    recordingWorklet.setFillLength(recordingWorklet.numberOfFrames);
                    fileBox.ifSome(box => box.endInSeconds.setValue(totalSeconds));
                }
                else {
                    terminator.terminate();
                    currentTake = Option.None;
                }
            }, false));
        }));
        return terminator;
    };
})(RecordAudio || (RecordAudio = {}));
